import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { Modal } from './Modal';
import { FlashItem } from './types';

const SPEED = 70; // pixels per second: a comfortable reading pace

/** Loads the flash-news headlines, refreshing every few minutes and whenever `version` changes. */
function useFlash(version: number, pub: boolean) {
  const [items, setItems] = useState<FlashItem[]>([]);
  const load = useCallback(() => {
    (pub ? api.publicFlash() : api.flash())
      .then(setItems)
      .catch(() => undefined); // keep what is shown if the server is briefly unreachable
  }, [pub]);
  useEffect(() => {
    load();
    const t = window.setInterval(load, 5 * 60 * 1000);
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, version]);
  return items;
}

/** The scrolling flash-news bar (right to left): slot 1 under the top navigation, slot 2 above the page controls. Shows nothing when there are no headlines. */
export function FlashTicker({ version = 0, slot = 1, pub = false }: { version?: number; slot?: 1 | 2; pub?: boolean }) {
  const all = useFlash(version, pub);
  const items = all.filter((i) => (i.slot ?? 1) === slot);
  const setRef = useRef<HTMLDivElement>(null);
  const [seconds, setSeconds] = useState(30);
  const key = items.map((i) => i.id + ':' + i.text).join('|');

  useLayoutEffect(() => {
    const el = setRef.current;
    if (!el) return;
    const measure = () => setSeconds(Math.max(12, el.scrollWidth / SPEED));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [key]);

  if (!items.length) return null;
  const set = (hidden: boolean) => (
    <div className="flash-set" ref={hidden ? undefined : setRef} aria-hidden={hidden || undefined}>
      {items.map((i) => (
        <span className="flash-item" key={i.id}>
          {i.text}
        </span>
      ))}
    </div>
  );
  return (
    <section className={'flash' + (slot === 2 ? ' flash2' : '')} aria-label={slot === 2 ? 'Advertisement' : 'Flash news'} role="marquee">
      <span className="flash-label">
        <span className="flash-dot" />{' '}
        {slot === 2 ? (
          <>
            <span className="lbl-long">ADVERTISEMENT</span>
            <span className="lbl-short">ADVERT</span>
          </>
        ) : (
          'FLASH'
        )}
      </span>
      <div className="flash-window">
        <div className="flash-track" style={{ animationDuration: seconds + 's' }}>
          {set(false)}
          {set(true)}
        </div>
      </div>
    </section>
  );
}

/** Add, edit and delete the day's flash-news headlines. */
export function FlashDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const [all, setAll] = useState<FlashItem[] | undefined>(undefined);
  const [slot, setSlot] = useState<1 | 2>(1);
  const items = all?.filter((i) => (i.slot ?? 1) === slot);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reload = useCallback(() => {
    api
      .flash()
      .then(setAll)
      .catch(() => setAll([]));
  }, []);
  useEffect(reload, [reload]);

  const run = async (fn: () => Promise<unknown>, fail: string) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      reload();
      onChanged();
      return true;
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : fail);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    const ok = await run(() => (editing != null ? api.editFlash(editing, t) : api.addFlash(t, slot)), 'Could not save the headline.');
    if (ok) {
      setText('');
      setEditing(null);
    }
  };

  return (
    <Modal title="Flash news" onClose={onClose}>
      <p className="muted">
        Two bars scroll headlines from right to left for everyone who is signed in: one under the top menu (FLASH) and one above the page controls (ADVERTISEMENT). Both are
        also printed on every page or clipping that is shared. Keep each headline short. They are removed automatically at midnight.
      </p>
      <div className="flash-tabs" role="tablist">
        {([1, 2] as const).map((n) => (
          <button
            key={n}
            role="tab"
            aria-selected={slot === n}
            className={'btn' + (slot === n ? ' primary' : '')}
            onClick={() => {
              setSlot(n);
              setEditing(null);
              setText('');
            }}
          >
            {n === 1 ? 'Top bar (under the menu)' : 'Advertisement bar (bottom, above the controls)'}
          </button>
        ))}
      </div>
      <div className="flash-add">
        <input
          type="text"
          value={text}
          maxLength={300}
          placeholder={editing != null ? 'Edit the headline' : 'Type a headline and press Enter'}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          autoFocus
        />
        <button className="btn primary" onClick={submit} disabled={!text.trim() || busy}>
          {editing != null ? 'Save' : 'Add'}
        </button>
        {editing != null && (
          <button
            className="btn"
            onClick={() => {
              setEditing(null);
              setText('');
            }}
          >
            Cancel
          </button>
        )}
      </div>
      {err && <p className="error">{err}</p>}
      {all === undefined && <p className="muted">Loading…</p>}
      {items && items.length === 0 && <p className="hint">No headlines yet. The bar is hidden until you add one.</p>}
      <ul className="flash-list">
        {(items || []).map((i) => (
          <li key={i.id}>
            <span>{i.text}</span>
            <button
              className="btn"
              disabled={busy}
              onClick={() => {
                setEditing(i.id);
                setText(i.text);
              }}
            >
              Edit
            </button>
            <button className="btn danger" disabled={busy} onClick={() => run(() => api.deleteFlash(i.id), 'Could not delete the headline.')}>
              Delete
            </button>
          </li>
        ))}
      </ul>
      {!!items?.length && (
        <div className="actions">
          <button
            className="btn danger"
            disabled={busy}
            onClick={() => window.confirm('Delete all flash news headlines?') && run(() => api.deleteAllFlash(slot), 'Could not delete the headlines.')}
          >
            Delete all
          </button>
        </div>
      )}
    </Modal>
  );
}
